import Cookies from 'js-cookie';
import Hogan from 'hogan.js';
import hoganHelpers from '../../helpers';
import { formatPriceWithComparison, responsiveImage, saveForConversionTracking } from '../../utils';

const buildQuickshopHtml = (card, additionalProductData) => {
  const variants = additionalProductData?.variants;
  const productId = additionalProductData?.id;
  const isBundleProduct = additionalProductData?.isBundleProduct;
  const productHandle = additionalProductData?.handle || '#';

  if (!variants || variants.length <= 0) return;

  let allVariantsOutOfStock = true;

  const options = variants
    .map(({ id, available, inventoryQuantity, optionLabel }) => {
      // Change allVariantsOutOfStock to false if a variant is available
      if (allVariantsOutOfStock && available) {
        allVariantsOutOfStock = false;
      }

      // Prepare Algolia tracking add to cart data
      const optionTrackingData = {
        index: card.dataset.algoliaIndex,
        eventName: 'Quickshop add to cart',
        queryID: card.dataset.algoliaQueryid,
        objectIDs: [String(id)],
      };

      if (window.algolia.customer) {
        optionTrackingData.authenticatedUserToken = `${window.algolia.customer}`;
      }

      return `
        <div class="options__option">
          <input
            id="${productId}-${id}"
            class="options__input visually-hidden"
            type="button"
            value="${id}"
            ${available ? '' : 'disabled'}
            data-algolia-tracking='${JSON.stringify(optionTrackingData)}'
            data-inventory-quantity="${inventoryQuantity}"
            data-quickshop-option
          />
          <label
            class="options__label${available ? '' : ' options__label--disabled'}"
            for="${productId}-${id}"
            role="button"
          >
            ${optionLabel}
          </label>
        </div>
      `;
    })
    .join('');

  return `
    <quick-shop class="quickshop">
      <div class="quickshop__popup">
        <div class="visibility-hidden" data-quickshop-options>
          ${
            allVariantsOutOfStock
              ? `
              <div class="quickshop__out-of-stock">
                Out of stock
              </div>
            `
              : ''
          }
          ${
            !isBundleProduct
              ? `
              <div class="quickshop__options options is-flex is-justified-start is-aligned-center">
                ${options}
              </div>
            `
              : ''
          }
        </div>
        ${
          !isBundleProduct
            ? `
          <button class="button button--secondary button--full-width quickshop__add-btn" data-quickshop-options-toggle>
            Quick Add
          </button>
          `
            : `
            <a href="/products/${productHandle}" class="button button--secondary button--full-width">
              Select your bundle
            </a>
          `
        }
      </div>
      <product-form class="product-form">
        <form
          method="post"
          action="/cart/add"
          id="product-card-form-${productId}"
          accept-charset="UTF-8"
          class="form"
          enctype="multipart/form-data"
          novalidate="novalidate"
          data-type="add-to-cart-form"
        >
          <input type="hidden" name="id" value="${variants[0]?.id}" />
          <input type="hidden" name="inventory_quantity" value="${variants[0]?.inventoryQuantity}" />

          <button
            type="submit"
            name="add"
            class="visually-hidden"
            data-add-to-cart-behavior="${window.algolia.addToCartBehavior}"
          ></button>
        </form>
      </product-form>
    </quick-shop>
  `;
};

const productCardDomChange = (card, additionalProductData) => {
  const cardHandle = card.dataset.handle;
  const { id, handle, title, vendor, isBundleProduct, imgUrl, imgAlternateUrl, badges, price, compareAtPrice, variants, variation_title } = additionalProductData;
  const imgSecondaryElem = card.querySelector('[data-product-card-image-secondary]');

  imgSecondaryElem.innerHTML = responsiveImage(title, imgAlternateUrl);

  // Quickshop
  const quickshopElem = card.querySelector('[data-product-card-quickshop]');
  quickshopElem.innerHTML = buildQuickshopHtml(card, additionalProductData);

  // No need to continue if we're hovering on the initial card
  if (cardHandle === handle) return;

  // Change card handle
  card.dataset.handle = handle;

  // Change primary image
  const imgPrimaryElem = card.querySelector('[data-product-card-image-primary]');
  imgPrimaryElem.innerHTML = responsiveImage(title, imgUrl);

  // Change badges
  const badgesElem = card.querySelector('[data-product-card-badges]');
  badgesElem.innerHTML = unescapeHTML(badges);

  // Change vendor
  const vendorElem = card.querySelector('[data-product-card-vendor]');
  if (variation_title && variation_title !== '') {
    vendorElem.innerHTML = `${vendor} • <span class="weight-regular">${variation_title}</span>`;
  } else {
    vendorElem.innerHTML = vendor;
  }

  // Change title
  const titleElem = card.querySelector('[data-product-card-title]');
  titleElem.innerHTML = title;

  // Change price
  const priceElem = card.querySelector('[data-product-card-price]');
  priceElem.innerHTML = formatPriceWithComparison(price, compareAtPrice);

  // Change product page urls
  card.querySelectorAll('[data-product-card-link]').forEach((anchor) => {
    anchor.href = `/products/${handle}`;
  });

  // Cange Wishlist Trigger Attributes
  const wishlistElem = card.querySelector('[data-wishlist-action]');

  if (!wishlistElem || !window._swat) return;

  wishlistElem.dataset.productId = id;
  wishlistElem.dataset.variantId = variants[0]?.id;
  wishlistElem.dataset.productDu = `${window.location.origin}/products/${handle}`;
  wishlistElem.dataset.productIu = imgUrl ? imgUrl.replace(/\/(.*)\.(\w{2,4})/g, '/$1_620x620.$2') : '';
  wishlistElem.dataset.productPr = price;
  wishlistElem.dataset.productDt = title;
};

const handleCardHover = async (event) => {
  const elem = event.currentTarget;
  const card = elem.closest('[data-product-card]');
  const productHandle = card?.dataset?.handle;
  const loadingClass = 'product-card--is-loading';

  if (!productHandle) return;

  try {
    card.classList.add(loadingClass);
    const additionalProductData = await fetchAdditionalProductData(productHandle);

    if (!additionalProductData) {
      throw new Error('No additionalProductData returned');
    }

    productCardDomChange(card, additionalProductData);
  } catch (error) {
    console.error('Error getting additional product data:', error);
  } finally {
    card.classList.remove(loadingClass);
  }
};

const handleSwatchClick = async (event) => {
  event.preventDefault();
  event.stopPropagation();

  const elem = event.currentTarget;
  const card = elem.closest('[data-product-card]');
  const swatchProductHandle = elem.dataset?.productCardColorId;
  const loadingClass = 'product-card--is-loading';

  if (!swatchProductHandle) return;

  try {
    card.classList.add(loadingClass);
    const additionalProductData = await fetchAdditionalProductData(swatchProductHandle);

    if (!additionalProductData) {
      throw new Error('No additionalProductData returned');
    }

    const cardSwatches = elem.closest('[data-product-card]').querySelectorAll('[data-product-card-color-id]');
    const activeSwatchClass = 'product-card-colors__link--is-active';

    cardSwatches.forEach((cardSwatch) => cardSwatch.classList.remove(activeSwatchClass));
    elem.classList.add(activeSwatchClass);

    productCardDomChange(card, additionalProductData);
  } catch (error) {
    console.error('Error getting additional product data:', error);
  } finally {
    card.classList.remove(loadingClass);
  }
};

const loadWishlist = (productList) => {
  let count = 0;

  return new Promise((resolve, reject) => {
    // If already loaded, return before starting interval
    if (productList.wishlistLoaded) {
      resolve(productList.wishlistItems);

      return;
    }

    const interval = setInterval(() => {
      count++;

      if (productList.wishlistLoaded) {
        clearInterval(interval);

        resolve(productList.wishlistItems);
      }

      if (count === 100) {
        clearInterval(interval);
        reject('Wishlist app not loaded');
      }
    }, 100);
  });
};

const renderHits = (renderOptions, isFirstRender) => {
  const { hits, widgetParams } = renderOptions;

  const { container, productList, type } = widgetParams;

  if (isFirstRender) {
    container.addEventListener('click', (e) => {
      let el = e.target;
      let hit;

      // If quickshop action
      if (el.closest('[data-product-card-quickshop]')) return;

      // If swatches action
      if (el.closest('[data-product-card-swatches]')) return;

      // If wishlist button
      if (el.closest('[data-wishlist-action]')) return;

      // If collection callout
      if (el.closest('.collection-callout')) return;

      e.preventDefault();

      do {
        if (el instanceof Element && el.matches('.product-card')) {
          hit = el;
          break;
        }
        el = el.parentNode;
      } while (el && el !== container);

      // Stop if hit not found
      if (!hit) return;

      const link = `/products/${hit.dataset.handle}`;

      const clickData = {
        index: hit.dataset.algoliaIndex,
        eventName: `${type} - product click`,
        queryID: hit.dataset.algoliaQueryid,
        objectIDs: [hit.dataset.algoliaObjectid],
        positions: [Number(hit.dataset.algoliaPosition)],
        userToken: Cookies.get('_ALGOLIA'),
      };

      // Set the logged in customer user token
      if (window.algolia.customer) {
        clickData.authenticatedUserToken = `${window.algolia.customer}`;
      }

      // Send the click event
      aa('clickedObjectIDsAfterSearch', clickData);

      saveForConversionTracking(clickData);

      window.location.href = link;
    });

    return;
  }

  if (hits.length > 0) {
    const template = Hogan.compile(document.querySelector('#product_list_card').innerHTML, { delimiters: '[[ ]]' });

    container.innerHTML = hits
      .map((hit) =>
        template.render({
          ...hit,
          helpers: hoganHelpers,
        })
      )
      .join('');

    // Start wishlist
    loadWishlist(productList).then((products) => {
      const buttonSelector = '[data-wishlist-action]';

      // Loop over active products and change state to active
      products.forEach((product) => {
        container.querySelectorAll(`${buttonSelector}.product_${product.empi}`).forEach((el) => el.classList.add('swym-added'));
      });

      // Loop over all wishlist buttons to display them and add event listeners
      container.querySelectorAll(buttonSelector).forEach((button) => {
        button.classList.remove('visually-hidden');

        button.addEventListener('click', (e) => {
          e.preventDefault();

          // If not in wishlist. Add to wishlist
          if (!button.classList.contains('swym-added')) {
            const productData = {
              empi: parseInt(button.dataset.productId),
              epi: parseInt(button.dataset.variantId),
              du: button.dataset.productDu.toString(),
              iu: button.dataset.productIu.toString(),
              pr: parseInt(button.dataset.productPr * 100) / 100,
              dt: button.dataset.productDt.toString(),
            };

            window._swat.addToWishList(
              productData,
              (result) => {
                button.classList.add('swym-added');
                // Add item to state
                productList.wishlistItems.push(result);
              },
              false
            );
          } else {
            button.classList.remove('swym-added');

            window._swat.removeFromWishList(
              {
                empi: parseInt(button.dataset.productId),
                epi: parseInt(button.dataset.variantId),
              },
              (result) => {
                // Remove item from state
                productList.wishlistItems = productList.wishlistItems.filter((item) => {
                  if (parseInt(item.empi) == parseInt(result.empi) && parseInt(item.epi) == parseInt(result.epi)) {
                    return false;
                  }
                  return true;
                });
              }
            );
          }
        });
      });
    });

    // Attach event listeners
    const allProductCards = container.querySelectorAll('[data-product-card]');

    allProductCards.forEach((card) => {
      // Card hover events
      const hoverElem = card.querySelector('[data-product-card-inner]');

      hoverElem.removeEventListener('mouseenter', handleCardHover);
      hoverElem.addEventListener('mouseenter', handleCardHover, { once: true });

      // Swatches click events
      const cardSwatches = card.querySelectorAll('[data-product-card-color-id]');

      cardSwatches.forEach((swatch) => {
        swatch.removeEventListener('click', handleSwatchClick);
        swatch.addEventListener('click', handleSwatchClick);
      });

      if (card.querySelector('[data-show-remaining-colours]')) {
        card.querySelector('[data-show-remaining-colours]').addEventListener('click', (e) => {
          e.target.remove();

          card.querySelectorAll('.product-card-colors__link--hidden').forEach((el) => {
            el.classList.remove('product-card-colors__link--hidden');
          });
        });
      }
    });

    // Collection Callouts
    if (type === 'collection') {
      const algoliaContainer = container.closest('product-list');

      // Callout 1
      if (algoliaContainer.querySelector('[data-callout-1]') && hits.length >= 6) {
        const callout1 = algoliaContainer.querySelector('[data-callout-1]').cloneNode(true);

        container.insertAdjacentElement('beforeend', callout1);
      }

      // Callout 2
      if (algoliaContainer.querySelector('[data-callout-2]') && hits.length >= 13) {
        const callout2 = algoliaContainer.querySelector('[data-callout-2]').cloneNode(true);

        container.insertAdjacentElement('beforeend', callout2);
      }

      // Callout 3
      if (algoliaContainer.querySelector('[data-callout-3]') && hits.length >= 13) {
        const callout3 = algoliaContainer.querySelector('[data-callout-3]').cloneNode(true);

        container.insertAdjacentElement('beforeend', callout3);
      }
    }
  } else {
    container.innerHTML = `
      <div class="list-products__empty is-col-12">
        <h3>No ${type === 'collection' ? 'Products' : 'Results'} found</h3>

        <p class="text-gray">
          ${type === 'collection' ? 'Unfortunately there are no products available in this collection.' : "Unfortunately we couldn't find any results, please try a different query or remove filters."}
        </p>
      </div>
    `;
  }
};

export const products = window.instantsearch.connectors.connectHits(renderHits);
